import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import ContainerCard from '../../../../common/components/ContainerCard';
import {
  // POSITION_OTHER_CODE,
  // SPECIALTY_OTHER_CODE,
  OTHER_HR_FUNCTION_CODE,
  OTHER_NON_HR_FUNCTION_CODE,
} from '../../../../constants';
import { IChapterBilling } from '../../../../models/Chapter/IChapterBilling';
import { IBasicItem } from '../../../../models/IBasicItem';
import { IProfileContainer } from '../../../../models/Profile/IProfileContainer';
import { IProfileForm } from '../../../../models/Profile/IProfileForm';
import DropdownEdit from '../DropdownEdit';
// import MultiSelectDropdown from '../../../../common/components/MultiSelectDropdown';
import TextFieldEdit from '../TextFieldEdit';
import styles from './ProfileEditMode.module.css';
import { format, parse } from 'date-fns';
import Autocomplete from '../../../../common/components/Autocomplete';
import { getDnbName, getCollegeName } from '../../../../services';

type EditProps = {
  profileContainer: IProfileContainer;
  chaptersBilling: IChapterBilling[];
  chapterState: string;
  toggleEditMode: () => void;
  handleChapterSelect: (stateCode: string) => IChapterBilling[];
  saveProfile: (profileForm: IProfileForm) => Promise<any>;
};

const ProfileEditMode = (props: EditProps) => {
  const [chapters, setChapters] = useState<IChapterBilling[]>({} as IChapterBilling[]);
  const [employers, setEmployers] = useState<IBasicItem[]>({} as IBasicItem[]);
  const [universities, setUniversities] = useState<IBasicItem[]>({} as IBasicItem[]);
  const profileEdit = props.profileContainer?.profileEditModel;
  const profile = props.profileContainer?.profileModel;
  const [othersHrFunction, setOtherHrFunction] = useState<string>('');

  const getDateInHtmlFormat = (_date: string) => {
    var formattedDate = _date ? format(parse(_date, 'M/d/y', new Date()), 'yyyy-MM-dd') : '';
    return formattedDate;
  };

  const {
    register,
    unregister,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
    getValues,
    clearErrors,
    trigger,
  } = useForm<IProfileForm>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      ...profile,
      graduationDate: getDateInHtmlFormat(profile.graduationDate),
      dateOfBirth: getDateInHtmlFormat(profile.birthDate),
    },
  });

  // const speciality = watch('specialtyCode');
  // const otherSpeciality = watch('specialtyOther');
  const chapterIdValue = watch('chapterId');

  const memberShipTypeStudent = profile.membershipType === 'STUDENT';

  // useEffect(() => {
  //   if (otherSpeciality && speciality !== SPECIALTY_OTHER_CODE) {
  //     setValue('specialtyOther', '');
  //   }
  // }, [speciality, otherSpeciality]);

  useEffect(() => {
    if (props.chaptersBilling && Array.isArray(props.chaptersBilling)) {
      setChapters(
        props.chaptersBilling.map(x => {
          return {
            label: x.label,
            value: x.value,
            isDualBilling: x.isDualBilling,
          };
        })
      );
    }
  }, [props.chaptersBilling]);

  useEffect(() => {
    setValue('chapterId', profile.chapterId);
    if (
      profile.gcrJobFunctionOthers &&
      profile.gcrJobFunctionSecondary[0] === OTHER_HR_FUNCTION_CODE
    ) {
      setOtherHrFunction(OTHER_HR_FUNCTION_CODE);
    }
    if (
      profile.gcrJobFunctionOthers &&
      profile.gcrJobFunctionSecondary[0] === OTHER_NON_HR_FUNCTION_CODE
    ) {
      setOtherHrFunction(OTHER_NON_HR_FUNCTION_CODE);
    }
  }, [profile]);

  const handleSecondaryFunction = (e: React.ChangeEvent<HTMLFormElement>) => {
    clearErrors('gcrJobFunctionOthers');
    if (e.target.value === OTHER_HR_FUNCTION_CODE) {
      setOtherHrFunction(OTHER_HR_FUNCTION_CODE);
      return setValue('gcrJobFunctionOthers', '');
    }
    if (e.target.value === OTHER_NON_HR_FUNCTION_CODE) {
      setOtherHrFunction(OTHER_HR_FUNCTION_CODE);
      return setValue('gcrJobFunctionOthers', '');
    }
    setValue('gcrJobFunctionOthers', '');
    setOtherHrFunction('');
    unregister('gcrJobFunctionOthers');
  };

  const isBetween = date => {
    if (!profile.birthDate && !date) return true;
    if (profile.birthDate && !date) return false;
    const _date = new Date(date).getFullYear();
    return profileEdit.birthYearsLowerLimit <= _date && _date <= profileEdit.birthYearsUpperLimit;
  };

  const isInFuture = date => {
    if (!date) return false;
    const _date = new Date(date);
    return _date > new Date();
  };

  const createDates = (start: number, end: number) => {
    const years = [];
    for (let i = start; i < end; i++) {
      years.push({
        label: i.toString(),
        value: i.toString(),
      });
    }
    return years;
  };

  const onChangeEmployer = (keys: string) => {
    setValue('companySelected', '');
    setEmployers([]);
    if (keys && keys.length >= 3) {
      getDnbName(profile.dnbToken, keys).then((response: any) => {
        setEmployers(response.data as IBasicItem[]);
      });
    }
  };

  const onChangeUniversity = (keys: string) => {
    setUniversities([]);
    if (keys && keys.length >= 3) {
      getCollegeName(keys).then((response: any) => {
        setUniversities(response.data as IBasicItem[]);
      });
    }
  };

  const emailValidation = email => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    if (email.trim() === '') {
      return;
    }
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
  };

  const onSelectEmployer = (selectedValue: IBasicItem) => {
    setValue('companySelected', selectedValue.label, { shouldValidate: true, shouldTouch: true });
    setValue('companyDnbNumber', selectedValue.value);
    setValue('companyName', selectedValue.label, { shouldValidate: true, shouldTouch: true });
    setEmployers([]);
    clearErrors('companyName');
    trigger('companyName', { shouldFocus: true });
    return null;
  };

  const onSelectUniversity = (selectedValue: IBasicItem) => {
    setValue('university', selectedValue.label, { shouldValidate: true });
    setValue('universityDnbNumber', selectedValue.value);
    setValue('universitySelected', selectedValue.label);
    setUniversities([]);
    clearErrors('university');
    trigger('university', { shouldFocus: true });
    return null;
  };

  const validateCompanyName = (data: string) => {
    const companySelected = getValues('companySelected');
    return data != null && companySelected?.length > 0;
  };

  const validateUniversity = (data: string) => {
    const universitySelected = getValues('universitySelected');
    return data != null && universitySelected?.length > 0;
  };

  const handleDualBillingChapterSelect = (value: string) => {
    const chapter = chapters.find(x => x.value === value);
    setValue('chapterId', value);
    if (chapter && chapter.isDualBilling) {
      setValue('isChapterDualBilling', true);
    } else {
      setValue('isChapterDualBilling', false);
    }
  };

  const chapterDualBillingNotPaid = () => {
    return getValues('isChapterDualBilling') && chapterIdValue !== profile.chapterId;
  };

  const isChapterAffiliation = () => {
    return !(props.chapterState !== '' && chapterIdValue === '');
  };

  const handleChapterState = e => {
    setValue('chapterId', '');
    setValue('isChapterDualBilling', false);
    props.handleChapterSelect(e.target.value);
  };

  return (
    <form
      onSubmit={handleSubmit(data => {
        props.saveProfile(data);
      })}
    >
      <ContainerCard>
        <div className={styles.container}>
          <div className={styles.title}>Personal information</div>
          <DropdownEdit
            className={styles.shortenfield}
            propStyle={styles.dropDownProp}
            label='prefix'
            items={profileEdit?.prefixes}
            placeholderLabel={'Select prefix'}
            {...register('prefix')}
          />

          <TextFieldEdit
            label='First Name '
            propStyle={styles.textPropGray}
            property={'firstName'}
            register={register}
            required
            disabled={true}
          />
          <p>{errors.firstName && 'This field is required'}</p>

          <TextFieldEdit
            label='Middle Name'
            propStyle={styles.textPropGray}
            property={'middleName'}
            register={register}
            disabled={true}
          />

          <div className={styles.lastNameContainer}>
            <div className={styles.shortTextField}>
              <TextFieldEdit
                label='Last Name '
                propStyle={styles.textPropGray}
                property={'lastName'}
                register={register}
                required
                disabled={true}
              />
            </div>
            <div className={`${styles.iconContainer}`}>
              <div className={`${styles.iconInfo} ${styles.tooltip} ${styles.tooltiptext}`}>
                <span className={styles.tooltiptext}>
                  Note: Please call Customer Experience at 800.283.7476 to change how your first and
                  last name appear.
                </span>
              </div>
            </div>
          </div>
          <p>{errors.lastName && 'This field is required'}</p>

          <DropdownEdit
            className={styles.shortenfield}
            label='suffix'
            propStyle={styles.dropDownProp}
            items={profileEdit?.suffixes}
            placeholderLabel={'Select suffix'}
            {...register('suffix')}
          />

          {/* <MultiSelectDropdown
            label='HR Interest'
            options={}
          /> */}

          <TextFieldEdit
            label='Preferred Name '
            propStyle={styles.textProp}
            property={'nickName'}
            register={register}
            // error={!!errors.nickName}
            // required
          />
          {/* <p className={styles.error}>{errors.nickName && 'This field is required'}</p> */}

          <div className={clsx(styles.labelName, errors.dateOfBirth && styles.error)}>
            Birthday{' '}
          </div>
          {/* {<span className={styles.error}>*</span>} */}
          <input
            className={clsx(
              styles.inputDate,
              styles.shortenfield,
              errors.dateOfBirth && styles.errorDateField
            )}
            type='date'
            {...register('dateOfBirth', { validate: { isBetween } })}
          />
          {/* <p className={styles.error}>
            {errors.dateOfBirth?.type === 'required' && 'Please enter date of birth'}
          </p> */}
          <p className={styles.error}>
            {errors.dateOfBirth?.type === 'isBetween' &&
              `Birth date have to be between ${profileEdit.birthYearsLowerLimit} and ${profileEdit.birthYearsUpperLimit}`}
          </p>

          <DropdownEdit
            label='Sex'
            propStyle={styles.dropDownProp}
            items={profileEdit?.gcrGenderSexList}
            placeholderLabel={'Select an Identification'}
            {...register('gcrGenderSex')}
          />

          <DropdownEdit
            label='Race/Ethnicity'
            propStyle={styles.dropDownProp}
            items={profileEdit?.gcrEthnicityRaceList}
            placeholderLabel={'Select an Ethnicity'}
            {...register('gcrEthnicityRace')}
          />

          <DropdownEdit
            label='Student'
            propStyle={styles.dropDownProp}
            items={profileEdit?.gcrStudentList}
            placeholderLabel={'Select a level'}
            {...register('gcrStudent')}
          />

          <DropdownEdit
            label='HR Interest'
            multiple
            propStyle={styles.dropDownProp}
            items={profileEdit?.gcrJobFunctionList}
            {...register('gcrHrInterest')}
          />

          <DropdownEdit
            label='Content Format Preference'
            multiple
            propStyle={styles.dropDownProp}
            items={profileEdit?.gcrContentFormatPreferenceList}
            {...register('gcrContentFormatPreference')}
          />

          <DropdownEdit
            label='Content Channel Preference'
            multiple
            propStyle={styles.dropDownProp}
            items={profileEdit?.gcrContentChannelPreferenceList}
            {...register('gcrContentChannelPreference')}
          />

          {/* <DropdownEdit
            label='Gender Identification'
            propStyle={styles.dropDownProp}
            items={profileEdit?.genders}
            placeholderLabel={'Select an Identification'}
            {...register('gender')}
          /> */}

          {/* <DropdownEdit
            label='Ethnicity'
            propStyle={styles.dropDownProp}
            items={profileEdit?.ethnicities}
            placeholderLabel={'Select an Ethnicity'}
            {...register('ethnicity')}
          /> */}

          <DropdownEdit
            label='Primary Language'
            propStyle={styles.dropDownProp}
            items={profileEdit?.primaryLanguages}
            placeholderLabel={'Select a Language'}
            {...register('primaryLanguage')}
          />

          <DropdownEdit
            label='Highest Level of Education'
            propStyle={styles.dropDownProp}
            items={profileEdit?.educationLevels}
            placeholderLabel={'Select a Level'}
            {...register('educationLevel')}
          />

          <TextFieldEdit
            label='Secondary Email'
            propStyle={styles.noteContainer}
            property={'altEmail'}
            register={register}
            validationFn={emailValidation}
            note
          />

          {errors?.altEmail?.message && <p className={styles.error}>{errors.altEmail.message}</p>}

          <div className={styles.professionalInformation}>
            <div className={styles.title}>Professional information</div>
            <div className={clsx(styles.container, styles.textProp)}>
              <label>Company Name </label>
              <span style={{ color: 'var(--shrmRed)' }}>*</span>
              <Autocomplete
                name='companyName'
                defaultValue={profile.companyName}
                suggestions={employers}
                register={register}
                onChange={onChangeEmployer}
                onSelect={onSelectEmployer}
                validate={validateCompanyName}
                required={true}
              />
            </div>

            <p className={styles.error}>
              {errors.companyName &&
                errors.companyName?.type === 'required' &&
                'This field is required'}
            </p>
            <p className={styles.error}>
              {errors.companyName && errors.companyName?.type !== 'required' && 'Invalid Company'}
            </p>
            {/* <DropdownEdit
              label='HR Department Size '
              propStyle={styles.dropDownProp}
              items={profileEdit?.hrDeptSizes}
              placeholderLabel={'Select a Size'}
              error={!!errors.hrDepartmentSize}
              {...register('hrDepartmentSize', { required: !memberShipTypeStudent })}
              required={!memberShipTypeStudent}
            />
            <p className={styles.error}>{errors.hrDepartmentSize && 'This field is required'}</p> */}
            <DropdownEdit
              label='Industry'
              propStyle={styles.dropDownProp}
              items={profileEdit?.gcrIndustryList}
              placeholderLabel={'Select an Industry'}
              {...register('gcrIndustry')}
            />
            <DropdownEdit
              label='Company Size'
              propStyle={styles.dropDownProp}
              items={profileEdit?.gcrCompanySizeList}
              placeholderLabel={'Select a Size'}
              {...register('gcrCompanySize')}
            />
            <DropdownEdit
              label='Primary Job Function (Specialty)'
              propStyle={styles.dropDownProp}
              items={profileEdit?.gcrJobFunctionList}
              placeholderLabel={'Select a specialty'}
              {...register('gcrJobFunctionPrimary')}
            />
            <DropdownEdit
              label='Secondary Job Function (Specialty)'
              multiple
              propStyle={styles.dropDownProp}
              items={profileEdit?.gcrJobFunctionList}
              {...register('gcrJobFunctionSecondary')}
              onChange={e => handleSecondaryFunction(e)}
            />
            {[OTHER_HR_FUNCTION_CODE, OTHER_NON_HR_FUNCTION_CODE].includes(othersHrFunction) && (
              <TextFieldEdit
                label='Other Secondary Job Function'
                propStyle={styles.textProp}
                property={'gcrJobFunctionOthers'}
                register={register}
                required
                error={!!errors.gcrJobFunctionOthers}
                message={
                  'This field has a character limit of 255 characters. Please shorten your input'
                }
                maxLength={256}
              />
            )}
            {errors?.gcrJobFunctionOthers?.message && (
              <p className={styles.error}>{errors.gcrJobFunctionOthers.message}</p>
            )}
            {[OTHER_HR_FUNCTION_CODE, OTHER_NON_HR_FUNCTION_CODE].includes(othersHrFunction) &&
              errors?.gcrJobFunctionOthers && (
                <p className={styles.error}>Please specify the other HR or non-HR function</p>
              )}

            <DropdownEdit
              label='Job Level'
              propStyle={styles.dropDownProp}
              items={profileEdit?.gcrJobLevelList}
              placeholderLabel={'Select a position level'}
              {...register('gcrJobLevel')}
            />
            <TextFieldEdit
              label='Job Title '
              propStyle={styles.textProp}
              property={'jobTitle'}
              error={!!errors.jobTitle}
              register={register}
              required
            />
            <p className={styles.error}>{errors.jobTitle && 'This field is required'}</p>
            {/* archived <DropdownEdit
              label='Speciality '
              propStyle={styles.dropDownProp}
              items={profileEdit?.specialties}
              placeholderLabel={'Select a Specialty '}
              {...register('specialtyCode', { required: true })}
              error={errors.specialtyCode}
              required
            />
            <p className={styles.error}>{errors.specialtyCode && 'This field is required'}</p>
            {speciality === SPECIALTY_OTHER_CODE && (
              <TextFieldEdit
                propStyle={styles.textProp}
                property={'specialtyOther'}
                error={!!errors.specialtyOther}
                placeholder='Other Specialty'
                register={register}
              />
            )} */}

            <DropdownEdit
              label='Started Working in HR '
              propStyle={styles.dropDownProp}
              items={createDates(
                profileEdit.yearsStartedHRLowerLimit,
                profileEdit.yearsStartedHRUpperLimit
              )}
              placeholderLabel={'Select a Year'}
              {...register('yearStartedHR', { required: !memberShipTypeStudent })}
              error={errors.yearStartedHR}
              required={!memberShipTypeStudent}
            />
            <p className={styles.error}>{errors.yearStartedHR && 'This field is required'}</p>
            {/* <DropdownEdit
              label='Which Industry are you in? '
              propStyle={styles.dropDownProp}
              items={profileEdit?.industryCodes}
              placeholderLabel={'Select an Industry'}
              {...register('industryType', { required: !memberShipTypeStudent })}
              error={errors.industryType}
              required={!memberShipTypeStudent}
            />
            <p className={styles.error}>{errors.industryType && 'This field is required'}</p> */}
            <DropdownEdit
              label="Which Best Describes Your Boss's Title? "
              propStyle={styles.dropDownProp}
              items={profileEdit?.supervisorPositions}
              placeholderLabel={'Select a Title'}
              {...register('supervisorsTitle', { required: !memberShipTypeStudent })}
              error={errors.supervisorsTitle}
              required={!memberShipTypeStudent}
            />
            <p className={styles.error}>{errors.supervisorsTitle && 'This field is required'}</p>
          </div>

          {memberShipTypeStudent && (
            <div className={styles.education}>
              <div className={styles.title}>Education information</div>
              <DropdownEdit
                label='What is your major?'
                propStyle={styles.dropDownProp}
                items={profileEdit?.academicMajors}
                placeholderLabel={'Select a Major'}
                error={!!errors.academicMajor}
                selectedValue={profile.academicMajor}
                {...register('academicMajor', { required: true })}
                required
              />
              <p className={styles.error}>{errors.academicMajor && 'This field is required'}</p>
              <div className={clsx(styles.container, styles.textProp)}>
                <label>Where are you enrolled?</label>
                <span style={{ color: 'var(--shrmRed)' }}>*</span>
                <Autocomplete
                  name='university'
                  defaultValue={profile.university}
                  suggestions={universities}
                  register={register}
                  onChange={onChangeUniversity}
                  onSelect={onSelectUniversity}
                  validate={validateUniversity}
                  required={true}
                />
              </div>
              <p className={styles.error}>
                {errors.university &&
                  errors.university?.type === 'required' &&
                  'This field is required'}
              </p>
              <p className={styles.error}>
                {errors.university &&
                  errors.university?.type !== 'required' &&
                  'Invalid University'}
              </p>
              <div className={clsx(styles.labelName, errors.graduationDate && styles.error)}>
                When do you plan to graduate?
              </div>
              {<span className={styles.error}>*</span>}
              <input
                className={clsx(styles.inputDate, errors.graduationDate && styles.errorDateField)}
                type='date'
                {...register('graduationDate', { required: true, validate: { isInFuture } })}
              />
              <p className={styles.error}>
                {errors.graduationDate?.type === 'required' && 'Please enter date of birth'}
              </p>
              <DropdownEdit
                label='What kind of degree are you pursuing?'
                propStyle={styles.dropDownProp}
                items={profileEdit?.enrollmentStatuses}
                placeholderLabel={'Select a Degree '}
                {...register('enrollmentStatus', { required: true })}
                error={errors.enrollmentStatus}
                required
              />
              <p className={styles.error}>{errors.enrollmentStatus && 'This field is required'}</p>
              <div className={styles.creditContainer}>
                <input
                  className={clsx(
                    styles.creditNumber,
                    styles.textProp,
                    errors.numberCredits && styles.errorDateField
                  )}
                  type='number'
                  {...register('numberCredits', { required: true })}
                />
                <span className={styles.per}> per </span>
                <DropdownEdit
                  className={styles.creditDropdown}
                  label=''
                  propStyle={styles.dropDownProp}
                  items={profileEdit?.creditTerms}
                  placeholderLabel={'Select a Term '}
                  {...register('creditTerm', { required: true })}
                  error={errors.creditTerm}
                  required
                />
              </div>
            </div>
          )}

          <div className={styles.chapterAffiliation}>
            <div className={styles.title}>Chapter affiliation</div>
            <div className=''>
              (Please Note: You must have separately registered with and paid applicable dues for
              your local chapter).
            </div>
            <DropdownEdit
              label="I'm a member of the following SHRM Chapter"
              propStyle={styles.dropDownProp}
              items={profileEdit?.chapterStates}
              placeholderLabel={'Select a State'}
              {...register('chapterState', {
                onChange: handleChapterState,
                validate: { isChapterAffiliation },
              })}
            />
            <DropdownEdit
              placeholderLabel={'Select a Chapter'}
              propStyle={styles.dropDownProp}
              items={chapters}
              selectedValue={chapterIdValue}
              disabled={!(props.chaptersBilling && props.chaptersBilling.length > 0)}
              {...register('chapterId', {
                onChange: e => handleDualBillingChapterSelect(e.target.value),
                validate: { isChapterAffiliation },
              })}
            />
            {chapterDualBillingNotPaid() && (
              <div className={styles.affiliationContainer}>
                Affiliation with some chapters may require payment of dues. Please{' '}
                <a href='https://store.shrm.org/dual-membership' target='_blank'>
                  continue
                </a>{' '}
                for additional information.
              </div>
            )}
            <div className={styles.footerLine}>
              <button
                type='submit'
                className={styles.buttonSave}
                disabled={!(isDirty && isValid && !chapterDualBillingNotPaid())}
              >
                SAVE
              </button>
              <Link to='/profile'>
                <button className={styles.buttonCancel} onClick={props.toggleEditMode}>
                  CANCEL
                </button>
              </Link>
            </div>
          </div>
        </div>
      </ContainerCard>
    </form>
  );
};
export default ProfileEditMode;
